import { useCallback, useEffect, useState } from "react";
import { Clock, Play, X, ListChecks } from "lucide-react";
import api, { whenVisible } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ISO_DAY = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * Floating reminder that surfaces when a person has task(s) scheduled for today
 * but hasn't started their time tracker yet. Encourages time-tracking hygiene.
 * - Polls /api/tasks + each candidate task's /timer every ~2 minutes
 * - Shows the first untracked task with a one-click "Start timer" action
 * - Dismissible for the current session
 */
export default function TimerReminderWidget() {
  const { user } = useAuth();
  const [candidate, setCandidate] = useState(null);
  const [dismissedIds, setDismissedIds] = useState(() => new Set());
  const [collapsed, setCollapsed] = useState(false);

  const check = useCallback(async () => {
    if (!user) return;
    try {
      const { data: tasks } = await api.get("/tasks");
      const today = ISO_DAY();
      // Any assignee-owned task whose scheduled window covers today, not yet complete
      const candidates = (tasks || []).filter((t) => {
        if (t.status === "complete") return false;
        const isMine = t.assigned_to === user.id || (t.assignees || []).includes(user.id);
        if (!isMine) return false;
        const start = (t.start_date || "").slice(0, 10) || null;
        const end = t.deadline ? t.deadline.slice(0, 10) : null;
        if (!start && !end) return false;
        const s = start || end;
        const e = end || start;
        return s <= today && today <= e;
      });
      // Skip everything the user has dismissed this session
      const remaining = candidates.filter((t) => !dismissedIds.has(t.id));
      // Find the first one that hasn't been timed today. We rely on the timer
      // endpoint's `total_seconds`: 0 means nothing logged yet.
      for (const t of remaining.slice(0, 5)) {
        try {
          const { data: timer } = await api.get(`/tasks/${t.id}/timer`);
          const totalSecs = timer?.total_seconds || 0;
          const isRunning = !!timer?.running;
          if (!isRunning && totalSecs === 0) {
            setCandidate(t);
            return;
          }
        } catch { /* ignore per-task timer failure */ }
      }
      setCandidate(null);
    } catch { /* silent */ }
  }, [user, dismissedIds]);

  useEffect(() => {
    if (!user) return;
    check();
    const id = setInterval(whenVisible(check), 2 * 60 * 1000); // every 2 minutes
    return () => clearInterval(id);
  }, [user, check]);

  const startTimer = async () => {
    if (!candidate) return;
    try {
      await api.post(`/tasks/${candidate.id}/timer/start`);
      toast.success(`Timer started — ${candidate.title}`);
      setCandidate(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start timer");
    }
  };

  const dismiss = () => {
    if (!candidate) return;
    setDismissedIds((prev) => new Set(prev).add(candidate.id));
    setCandidate(null);
  };

  if (!candidate || collapsed) return null;

  return (
    <div
      data-testid="timer-reminder-widget"
      className="fixed right-4 bottom-[15rem] z-40 max-w-xs w-[92vw] sm:w-[300px] rounded-xl border border-amber-500/40 bg-surface shadow-2xl shadow-black/70 overflow-hidden"
    >
      <div className="flex items-center justify-between px-3 py-2 bg-amber-500/15 border-b border-amber-500/30">
        <div className="flex items-center gap-2 text-amber-200">
          <Clock size={13} className="animate-pulse" />
          <div className="text-xs uppercase tracking-widest font-mono">Timer nudge</div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            data-testid="timer-reminder-hide"
            title="Hide until refresh"
            className="text-amber-100/50 hover:text-amber-100 p-1"
          >
            <ListChecks size={12} />
          </button>
          <button
            type="button"
            onClick={dismiss}
            data-testid="timer-reminder-dismiss"
            title="Not this one"
            className="text-amber-100/50 hover:text-amber-100 p-1"
            aria-label="Dismiss"
          >
            <X size={12} />
          </button>
        </div>
      </div>
      <div className="p-3">
        <div className="text-[0.7rem] text-amber-200/80 uppercase tracking-widest mb-1 font-mono">
          {candidate.client || "No client"}
        </div>
        <div className="text-sm text-amber-50 font-display leading-snug break-words">
          {candidate.title || "Untitled task"}
        </div>
        <div className="text-xs text-amber-100/60 font-mono mt-1.5">
          Due today · no time logged yet
        </div>
        <button
          type="button"
          onClick={startTimer}
          data-testid="timer-reminder-start"
          className="mt-3 w-full flex items-center justify-center gap-1.5 bg-amber-500/25 hover:bg-amber-500/40 border border-amber-500/50 text-amber-100 text-xs font-medium rounded-md py-2 transition"
        >
          <Play size={12} /> Start timer now
        </button>
      </div>
    </div>
  );
}
